// src/data/documentReview.js
//
// SECTION 543 (2026-09-30). Bob: "I think we need another tool in the lc and gc arsenal - an analytical tool that
// assess the documents we produce, spell, check, grammar check, fact check, prose check, etc." and "every document
// this platform produces is to have the same proof reading capabilities".
//
// Two halves. THIS file is the half that needs no network and cannot be wrong about what it checks: it reads a
// finished document against the record it was built from and reports -
//   FACT   a date, amount, rule or award in the document that is not on the record it was built from;
//   LEAK   template debris a reader must never see: "undefined", "null", "NaN", "[DATE]", "TBD", "XXX", "{", "}";
//   FORM   doubled words ("the the"), unbalanced quotation marks or brackets, a space before punctuation;
//   PROSE  an overstatement word (ruling 265: no embellishing, no exaggeration) and a sentence over 45 words.
// The other half (functions\documentReview.js) reads spelling, grammar and prose with the language service and
// reports only what it can point to word for word in the document.
//
// It REPORTS. It changes nothing in a document: a letter or a claim is fixed at its source (the claim's fields, the
// letter's inputs), so the next build is right. Ruling 265's limit governs both halves - a finding is stated from
// the document and the record, never supposed.
const s = (v) => (v == null ? '' : String(v)).trim();
const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

// The awards the platform's own documents cite (penaltyClaim.js) - an award outside this list, and outside the
// record handed in, is reported for checking; it is not called wrong.
export const KNOWN_AWARDS = Object.freeze(['29232', '29913', '27614', '26593', '21678', '19899', '18305', '18687', '33994']);

// Overstatement words (ruling 265). Reported as PROSE, never removed.
export const OVERSTATEMENT = Object.freeze(['clearly', 'obviously', 'blatantly', 'blatant', 'egregious', 'egregiously',
  'flagrant', 'flagrantly', 'outrageous', 'outrageously', 'undeniably', 'unquestionably', 'totally', 'completely',
  'absolutely', 'always', 'never', 'deliberately', 'intentionally', 'willfully', 'maliciously', 'shocking', 'unbelievable']);

// ---- dates ------------------------------------------------------------------------------------------------------
function iso(y, m, d) {
  const Y = +y, M = +m, D = +d;
  if (!(Y > 1900 && Y < 2200 && M >= 1 && M <= 12 && D >= 1 && D <= 31)) return '';
  return Y + '-' + String(M).padStart(2, '0') + '-' + String(D).padStart(2, '0');
}
// Every date written in a text, as { raw, iso }.
export function datesIn(text) {
  const t = String(text || '');
  const out = [];
  let m;
  const long = /\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2}),?\s+(\d{4})\b/gi;
  while ((m = long.exec(t))) out.push({ raw: m[0], iso: iso(m[3], MONTHS.indexOf(m[1].toLowerCase()) + 1, m[2]) });
  const isoRe = /\b(\d{4})-(\d{2})-(\d{2})\b/g;
  while ((m = isoRe.exec(t))) out.push({ raw: m[0], iso: iso(m[1], m[2], m[3]) });
  const us = /\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/g;
  while ((m = us.exec(t))) out.push({ raw: m[0], iso: iso(m[3], m[1], m[2]) });
  return out.filter(d => d.iso);
}
function normDate(v) {
  const x = s(v);
  if (!x) return '';
  const d = datesIn(x);
  if (d.length) return d[0].iso;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(x);
  return m ? iso(m[1], m[2], m[3]) : '';
}

// ---- amounts, rules, awards -------------------------------------------------------------------------------------
const money = (v) => { const n = Number(String(v).replace(/[$,\s]/g, '')); return isFinite(n) ? n.toFixed(2) : ''; };
export function amountsIn(text) {
  return (String(text || '').match(/\$\s?\d[\d,]*(?:\.\d{1,2})?/g) || []).map(raw => ({ raw, value: money(raw) }));
}
export function awardsIn(text) {
  const out = [];
  const re = /\bAwards?\s+((?:No\.\s*)?\d{4,6}(?:\s*(?:,|and|&)\s*(?:No\.\s*)?\d{4,6})*)/gi;
  let m;
  while ((m = re.exec(String(text || '')))) (m[1].match(/\d{4,6}/g) || []).forEach(no => out.push(no));
  return Array.from(new Set(out));
}
const ruleKey = (r) => s(r).toLowerCase().replace(/^(rule|article|art\.)\s*/i, '').replace(/[\s.]/g, '');
export function rulesIn(text) {
  const out = [];
  const re = /\b(?:Rule|Article)\s+([0-9IVXL]+[A-Za-z]?(?:\s*\([A-Za-z0-9]+\))*(?:\.\d+)?)/g;
  let m;
  while ((m = re.exec(String(text || '')))) out.push(m[0]);
  return Array.from(new Set(out));
}

// ---- the record a document was built from ----------------------------------------------------------------------
// facts = { dates: [], amounts: [], rules: [], awards: [], names: [] } - any may be missing; a check with no record
// to compare against is NOT run (and the report says which were run: ruling 194, an unperformed check is no pass).
export function factsFromClaim(c) {
  const claim = c || {};
  const dates = [claim.incidentDate, claim.presentedOn, claim.seniorityDate, claim.claimantSeniorityDate, claim.filedAt,
    claim.createdAt, claim.carrierAnswerDate, claim.appealDate, claim.conferenceDate]
    .concat(Array.isArray(claim.claimDates) ? claim.claimDates : [])
    .concat(Array.isArray(claim.dates) ? claim.dates : []);
  const amounts = [claim.totalAmountClaimed, claim.amountClaimed, claim.claimAmount, claim.totalAmount, claim.hourlyRate,
    claim.rate, claim.amountPaid, claim.settlementAmount];
  const rules = (Array.isArray(claim.matchedRules) ? claim.matchedRules : []).filter(r => r && !r.setAsideAt).map(r => r.rule);
  (datesIn(claim.applicableAgreement).map(d => d.iso)).forEach(d => dates.push(d));
  const names = [claim.claimantName, claim.violatorName, claim.presentedTo, claim.railroad];
  return { dates: dates.filter(Boolean), amounts: amounts.filter(v => v !== undefined && v !== null && v !== ''), rules: rules.filter(Boolean), awards: [], names: names.filter(Boolean) };
}

// Any record (a discipline case, an application, a meeting): every date written in it, every amount field, every
// name field, up to three levels deep. For documents built from something other than a claim.
export function factsFromRecord(rec) {
  const dates = [], amounts = [], names = [];
  const walk = (v, k, depth) => {
    if (v == null || depth > 3) return;
    if (typeof v === 'string') { datesIn(v).forEach(d => dates.push(d.iso)); if (/^\d{4}-\d{2}-\d{2}/.test(v)) dates.push(v.slice(0, 10)); if (/name$/i.test(k || '')) names.push(v); return; }
    if (typeof v === 'number') { if (/amount|rate|total|paid|owed/i.test(k || '')) amounts.push(v); return; }
    if (typeof v.toDate === 'function') { try { dates.push(v.toDate().toISOString().slice(0, 10)); } catch (e) { /* not a date */ } return; }
    if (Array.isArray(v)) { v.forEach(x => walk(x, k, depth + 1)); return; }
    if (typeof v === 'object') Object.keys(v).forEach(x => walk(v[x], x, depth + 1));
  };
  walk(rec, '', 0);
  return { dates, amounts, rules: [], awards: [], names };
}

// The claim record in a few plain lines, for the language review's fact check (what the document was built from).
export function claimRecordLine(c) {
  const x = c || {};
  const r = (Array.isArray(x.matchedRules) ? x.matchedRules : []).filter(m => m && !m.setAsideAt).map(m => m.rule).filter(Boolean);
  return [['Claimant', x.claimantName], ['Carrier', x.railroad], ['Agreement', x.applicableAgreement], ['Date of violation', x.incidentDate],
    ['Time', x.incidentTime24 || x.incidentTime], ['Location', x.eventLocation || x.workLocation], ['Presented to', x.presentedTo],
    ['Other employee', x.violatorName], ['Rules', r.join('; ')], ['Amount claimed', x.totalAmountClaimed]]
    .filter(([, v]) => v !== undefined && v !== null && String(v).trim()).map(([k, v]) => k + ': ' + v).join('\n');
}

// ---- the review ---------------------------------------------------------------------------------------------------
const LEAKS = [
  [/\bundefined\b/g, 'the word "undefined" - a blank field printed as code'],
  [/\bnull\b/g, 'the word "null" - a blank field printed as code'],
  [/\bNaN\b/g, '"NaN" - a number that could not be computed'],
  [/\[object Object\]/g, '"[object Object]" - a field printed as code'],
  [/\[(?:DATE|NAME|DATE OF RATIFICATION|INSERT[^\]]*|TBD|X+)\]/gi, 'a placeholder in square brackets'],
  [/\[NOT ON FILE\]/g, '"[NOT ON FILE]" - a field the record does not hold yet'],
  [/\bTBD\b|\bXXX+\b/g, 'a placeholder ("TBD" / "XXX")'],
  [/\{\{?[^}\n]{0,40}\}?\}/g, 'a template marker in braces'],
];

function excerptAround(text, index, len) {
  const a = Math.max(0, index - 30), b = Math.min(text.length, index + len + 30);
  return (a > 0 ? '…' : '') + text.slice(a, b).replace(/\s+/g, ' ') + (b < text.length ? '…' : '');
}

export function sentencesOf(text) {
  return String(text || '').replace(/\n{2,}/g, '\n').split(/(?<=[.!?])\s+(?=[A-Z“"(])|\n/).map(x => x.trim()).filter(Boolean);
}

export function localReview(text, facts, opts) {
  const t = String(text || '');
  const f = facts || {};
  const o = opts || {};
  const findings = [];
  const ran = [];
  const add = (type, severity, excerpt, note) => findings.push({ type, severity, excerpt, note, source: 'record' });

  // LEAK
  ran.push('template debris');
  LEAKS.forEach(([re, what]) => { let m; re.lastIndex = 0; while ((m = re.exec(t))) add('leak', 'fix', excerptAround(t, m.index, m[0].length), 'The document shows ' + what + '.'); });

  // FORM
  ran.push('doubled words and punctuation');
  { const re = /\b(\w+)[ \t]+\1\b/gi; let m; while ((m = re.exec(t))) { if (!/^\d+$/.test(m[1]) && !/^(that|had|is)$/i.test(m[1])) add('form', 'fix', excerptAround(t, m.index, m[0].length), '"' + m[1] + '" is written twice.'); } }
  { const re = /\s+[,.;:](?!\d)/g; let m; while ((m = re.exec(t))) add('form', 'fix', excerptAround(t, m.index, m[0].length), 'A space before punctuation.'); }
  const count = (re) => (t.match(re) || []).length;
  if (count(/\(/g) !== count(/\)/g)) add('form', 'fix', '', 'Brackets do not balance: ' + count(/\(/g) + ' opening, ' + count(/\)/g) + ' closing.');
  if (count(/“/g) !== count(/”/g)) add('form', 'fix', '', 'Quotation marks do not balance: ' + count(/“/g) + ' opening, ' + count(/”/g) + ' closing.');
  if (count(/"/g) % 2 === 1) add('form', 'fix', '', 'An odd number of straight quotation marks (") - one is not closed.');

  // FACT - each only when the record has something to compare against
  const recDates = new Set((f.dates || []).map(normDate).filter(Boolean));
  if (recDates.size) {
    ran.push('dates against the record');
    const seen = new Set();
    datesIn(t).forEach(d => { if (!recDates.has(d.iso) && !seen.has(d.iso) && !(o.today && d.iso === o.today)) { seen.add(d.iso); add('fact', 'check', d.raw, 'This date is not on the record this document was built from.'); } });
  }
  const recAmounts = new Set((f.amounts || []).map(money).filter(Boolean));
  if (recAmounts.size) {
    ran.push('amounts against the record');
    amountsIn(t).forEach(a => { if (!recAmounts.has(a.value)) add('fact', 'check', a.raw, 'This amount is not on the record this document was built from.'); });
  }
  const recRules = new Set((f.rules || []).map(ruleKey).filter(Boolean));
  if (recRules.size) {
    ran.push('rules against the claim');
    rulesIn(t).forEach(r => { const k = ruleKey(r); if (![...recRules].some(x => x === k || x.startsWith(k) || k.startsWith(x))) add('fact', 'check', r, 'This rule is not among the rules on the claim.'); });
  }
  ran.push('award numbers against the platform list');
  const knownAwards = new Set(KNOWN_AWARDS.concat((f.awards || []).map(s)));
  awardsIn(t).forEach(no => { if (!knownAwards.has(no)) add('fact', 'check', 'Award ' + no, 'This award is not in the platform’s award list or on the record - confirm it before the document goes out.'); });

  // PROSE (ruling 265)
  ran.push('overstatement and sentence length');
  const words = new RegExp('\\b(' + OVERSTATEMENT.join('|') + ')\\b', 'gi');
  { let m; while ((m = words.exec(t))) add('prose', 'check', excerptAround(t, m.index, m[0].length), '"' + m[0] + '" states more than a fact can prove: no embellishing, no exaggeration. Keep it only if the record shows it.'); }
  sentencesOf(t).forEach(x => { const n = x.replace(/\u201C[^\u201D]*\u201D|"[^"]*"/g, ' ').split(/\s+/).filter(Boolean).length; if (n > 45) add('prose', 'check', x.slice(0, 90) + (x.length > 90 ? '…' : ''), 'A ' + n + '-word sentence. Shorter reads clearer.'); });

  return { findings, ran };
}

// The server's findings, kept only where the excerpt is really in the document (nothing is reported that the reader
// cannot find on the page), merged after the record's own.
export function mergeFindings(local, server, text) {
  const t = String(text || '');
  const kept = (Array.isArray(server) ? server : []).filter(x => x && s(x.excerpt) && t.includes(s(x.excerpt)))
    .map(x => ({ type: ['spelling', 'grammar', 'prose', 'fact'].includes(x.type) ? x.type : 'prose', severity: x.type === 'spelling' || x.type === 'grammar' ? 'fix' : 'check',
      excerpt: s(x.excerpt), suggestion: s(x.suggestion), note: s(x.note), source: 'language' }));
  return (local || []).concat(kept);
}

export function summary(findings) {
  const f = Array.isArray(findings) ? findings : [];
  const fix = f.filter(x => x.severity === 'fix').length;
  const check = f.filter(x => x.severity === 'check').length;
  if (!f.length) return { tone: 'ok', line: 'No findings.' };
  return { tone: fix ? 'bad' : 'warn', line: [fix ? fix + ' to fix' : '', check ? check + ' to check' : ''].filter(Boolean).join(', ') + '.' };
}

// A penalty claim form (penaltyClaim.buildPenaltyClaim().form) as the words a reader sees, in order.
export function penaltyClaimText(form) {
  const f = form || {};
  const rows = (f.rows || []).flat().concat(f.full || []).map(r => (Array.isArray(r) ? s(r[0]) + ': ' + s(r[1]) : ''));
  const am = f.amount && typeof f.amount === 'object' ? f.amount : {};
  const amount = [am.hours ? 'Amount/Hours Claimed: ' + am.hours + ' ' + s(am.text) : s(am.text), am.total ? 'Total Amount Claimed: ' + am.total : ''];
  const substance = (f.substance || []).map(x => (typeof x === 'string' ? x : [s(x && x.label), s(x && x.text), x && x.quote ? '\u201C' + s(x.quote) + '\u201D' : ''].filter(Boolean).join(' ')));
  return [f.title, f.number ? 'Claim No. ' + f.number : '', ...rows, f.presentNote, ...amount, s(f.standing),
    'Substance of Claim:', ...substance, s(f.closing), ...(f.attachments || []).map(a => (typeof a === 'string' ? a : s(a && (a.text || a.label)))),
    s(f.payment), ...(f.signers || []).map(x => s(x && x.nameLine)), typeof f.receipt === 'string' ? f.receipt : '', f.serviceNote]
    .map(s).filter(Boolean).join('\n');
}

// A cache key for a document's text - the language half is asked once per distinct text.
export function textKey(text) {
  let h = 5381; const t = String(text || '');
  for (let i = 0; i < t.length; i++) h = ((h << 5) + h + t.charCodeAt(i)) | 0;
  return t.length + ':' + (h >>> 0).toString(36);
}
